import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Linkedin as LinkedinIcon } from "@/components/icons/linkedin";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { LinkedInDropzone } from "@/components/linkedin/LinkedInDropzone";
import { linkedInApi } from "@/services/linkedin-api";
import { isJobTerminal, statusLabel } from "@/types/linkedin";
import { toUserError } from "@/lib/api-error";

interface Props {
  freelancerId: string;
  freelancerNome?: string;
}

/**
 * Botão "Subir LinkedIn" pra ser usado dentro da página de detalhe do
 * freelancer. Abre dialog inline com dropzone single-file, mostra progresso e
 * o ReviewDialog ao finalizar.
 */
export function FreelancerLinkedInUploadButton({ freelancerId, freelancerNome }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) =>
      linkedInApi.uploadSingle(file, {
        freelancerId,
        onProgress: setUploadPct,
      }),
    onSuccess: (job) => {
      setUploadPct(null);
      setJobId(job.id);
    },
    onError: (e: Error) => {
      setUploadPct(null);
      toast({ title: "Erro no upload", description: toUserError(e).description, variant: "destructive" });
    },
  });

  const { data: job } = useQuery({
    queryKey: ["linkedin-job", jobId],
    queryFn: () => linkedInApi.getJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && !isJobTerminal(s) && s !== "review_ready" ? 1500 : false;
    },
  });

  useEffect(() => {
    if (!job) return;
    if (job.status === "review_ready") {
      setOpen(false);
      setJobId(null);
      navigate(`/linkedin/jobs/${job.id}/revisar`);
    } else if (job.status === "applied") {
      toast({ title: "Aplicado", description: "Perfil atualizado." });
      queryClient.invalidateQueries({ queryKey: ["freelancer", freelancerId] });
      setOpen(false);
      setJobId(null);
    } else if (job.status === "no_changes") {
      toast({ title: "Sem mudanças", description: "O PDF é idêntico ao último aplicado." });
      setOpen(false);
      setJobId(null);
    } else if (job.status === "failed") {
      toast({ title: "Falhou", description: job.error_message ?? "Erro", variant: "destructive" });
    }
  }, [job, freelancerId, queryClient, toast, navigate]);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <LinkedinIcon className="h-4 w-4 mr-2" /> Subir PDF do LinkedIn
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setJobId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Subir PDF do LinkedIn</DialogTitle>
            <DialogDescription>
              {freelancerNome ? <>O PDF será atrelado a <strong>{freelancerNome}</strong>.</> : null}
            </DialogDescription>
          </DialogHeader>

          {!job ? (
            <div className="space-y-3">
              <LinkedInDropzone
                multiple={false}
                disabled={upload.isPending}
                onFiles={(files) => upload.mutate(files[0])}
              />
              {uploadPct !== null && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span>Enviando pro S3...</span>
                    <span className="text-muted-foreground">{uploadPct}%</span>
                  </div>
                  <Progress value={uploadPct} className="h-1.5" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{statusLabel(job.status)}</Badge>
                <span className="text-xs text-muted-foreground">{job.original_filename}</span>
              </div>
              <Progress value={job.progress_pct} />
              {job.status === "awaiting_match" && (
                <div className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                  Não foi possível associar automaticamente. Vá para a tela /linkedin pra resolver.
                </div>
              )}
              {job.status === "failed" && (
                <div className="text-sm text-red-700 bg-red-50 p-2 rounded">
                  {job.error_message}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setJobId(null); }}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
